import React, { useEffect, useRef, useState } from 'react';
import { MzcrTemporaryStorage, PickedHealthDocument } from '@meddihub/mzcr-ui';
import useNotification from '../../../../Hooks/useNotification.hook';
import downloadHealthDocuments from '../../../Mzcr/Services/downloadHealthDocuments';
import { getMzcrClient } from '../../../Mzcr/Services/mzcrClient';
import ContentSkeleton from '../../../Base/Components/ContentSkeleton';
import { usePatientTreatmentContext } from './ContextProvider';
import MzcrSubTabs, {
	MzcrSubTabView,
	MzcrSubTabsRenderArgs,
} from './MzcrSubTabs';

interface Props {
	/** Switches the patient card to the Profile tab (missing data for the MZČR registry). */
	onOpenProfile?: () => void;
}

/**
 * Dočasné úložiště (MZČR) in the patient card – sub-tabs "Nová zásilka", "Odeslané" and
 * "Přijaté" (designs D1–D8), each showing only this patient's packages.
 *
 * Files sent from Zdravotnická dokumentace (design Z1/Z2) wait in the patient treatment context -
 * they are taken over here, downloaded and offered in "Nová zásilka".
 */
const PatientTreatmentTemporaryStorage: React.FC<Props> = ({
	onOpenProfile,
}) => {
	const { patientId, filesToSend, onFilesToSendTaken } =
		usePatientTreatmentContext();
	const { showNotificationAlert } = useNotification();
	// Decided once, on the first visit - the files are taken over right away
	const [initialView] = useState<MzcrSubTabView>(filesToSend ? 'new' : 'sent');
	const [isPreparing, setIsPreparing] = useState(!!filesToSend);
	const [initialDocuments, setInitialDocuments] = useState<
		PickedHealthDocument[] | undefined
	>();
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;
		if (filesToSend) {
			onFilesToSendTaken?.();
			downloadHealthDocuments(patientId, filesToSend)
				.then(documents => isMounted.current && setInitialDocuments(documents))
				.catch(e => {
					console.error(e);
					showNotificationAlert({
						content: 'component.eCard.sendViaTemporaryStorageFailed',
					});
				})
				.finally(() => isMounted.current && setIsPreparing(false));
		}
		return () => {
			isMounted.current = false;
		};
	}, []);

	const renderService = ({
		view,
		service,
		onViewChange,
		onNewReceivedCountChange,
	}: MzcrSubTabsRenderArgs) =>
		view === 'new' && isPreparing ? (
			<ContentSkeleton />
		) : (
			<MzcrTemporaryStorage
				key={service.rid}
				mzcrClient={getMzcrClient()}
				{...service}
				view={view}
				initialDocuments={view === 'new' ? initialDocuments : undefined}
				onViewChange={(next: MzcrSubTabView) => {
					// Sent or discarded - the next "Nová zásilka" starts empty
					setInitialDocuments(undefined);
					onViewChange(next);
				}}
				onNewReceivedCountChange={onNewReceivedCountChange}
			/>
		);

	return (
		<MzcrSubTabs
			translationPrefix='patientTreatment.temporaryStorage'
			initialView={initialView}
			onOpenProfile={onOpenProfile}
			renderService={renderService}
		/>
	);
};

export default PatientTreatmentTemporaryStorage;
